import React from 'react';
import { InputGroup, Button, FloatingLabel, Row, Col } from 'react-bootstrap';
import { Formik, Form, useFormikContext } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { map } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'usehooks-ts';

import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrderStatuses, tOrderStatus } from '@models/Order';
import { FormikControl, FormikSelect } from '@components/Base';
import { mergeInitialValues } from '@utils/formik';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { InsuranceTypesEnum, tInsuranceTypeEnum } from '@models/InsuranceType';

type FormValues = {
  search: string;
  status: OrderStatuses | '';
  policyType: PolicyTypes | '';
  insuranceType: InsuranceTypesEnum | '';
}

const FormForm = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { values } = useFormikContext<FormValues>();
  const { status, policyType, insuranceType } = values;

  useUpdateEffect(() => {
    setSearchParams(values);
  }, [status, policyType, insuranceType]);

  return (
    <Form>
      <Row>
        <Col xs={12} xl={6} className="mb-3">
          <InputGroup>
            <FloatingLabel label={t('insurancePolicy.search')} style={{ flex: '1 1 auto', width: '1%', minWidth: '0' }}>
              <FormikControl name="search" isValid={false} isInvalid={false} />
            </FloatingLabel>
            <Button type="submit">
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:policyType')}>
            <FormikSelect name="policyType" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(PolicyTypes, (value,) => (
                <option key={value} value={value}>{tPolicyType(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:insuranceType')}>
            <FormikSelect name="insuranceType" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(InsuranceTypesEnum, (value,) => (
                <option key={value} value={value}>{tInsuranceTypeEnum(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>

        <Col xs={4} xl={2} className="mb-3">
          <FloatingLabel label={t('common:status')}>
            <FormikSelect name="status" isValid={false} isInvalid={false}>
              <option value="">{t('common:all')}</option>
              {map(OrderStatuses, (value,) => (
                <option key={value} value={value}>{tOrderStatus(value)}</option>
              ))}
            </FormikSelect>
          </FloatingLabel>
        </Col>
      </Row >
    </Form>
  )
}

const PolicyControlForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams);
  const defaultValues: FormValues = { status: '', search: '', insuranceType: '', policyType: '' };
  const initialValues = mergeInitialValues(defaultValues, params);

  const handleSubmit = ({ search }: FormValues) => {
    setSearchParams({ search });
  }

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
      <FormForm />
    </Formik >
  );
}

export { PolicyControlForm };
